import { zodResolver } from '@hookform/resolvers/zod';
import { LoaderCircle, MessageSquare, Paperclip, Send } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectedFilesList } from './SelectedFilesList';

import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

function areSameFile(fileA: File, fileB: File) {
  return (
    fileA.name === fileB.name &&
    fileA.size === fileB.size &&
    fileA.lastModified === fileB.lastModified
  );
}

interface ReplyTicketComposerText {
  replyTicketTitle: string;
  replyPlaceholder: string;
  replyAttachFilesButton: string;
  replyImagesHint: string;
  sendMessageButton: string;
  addMessageOrImage: string;
  invalidFileType: (name: string) => string;
  maxFileSize: (name: string) => string;
  maxImagesPerMessage: string;
}

interface ReplyTicketFormValues {
  message: string;
  files: File[];
}

interface ReplyTicketComposerProps {
  text: ReplyTicketComposerText;
  isSubmitting: boolean;
  isDisabled: boolean;
  blockedMessage?: string | null;
  showBlockedMessage: boolean;
  maxAttachmentsPerMessage: number;
  maxImageSizeBytes: number;
  allowedMimeTypes: readonly string[];
  onSubmit: (values: ReplyTicketFormValues) => Promise<boolean>;
}

export function ReplyTicketComposer({
  text,
  isSubmitting,
  isDisabled,
  blockedMessage,
  showBlockedMessage,
  maxAttachmentsPerMessage,
  maxImageSizeBytes,
  allowedMimeTypes,
  onSubmit,
}: ReplyTicketComposerProps) {
  const schema = useMemo(
    () =>
      z
        .object({
          message: z.string(),
          files: z
            .array(z.custom<File>())
            .max(maxAttachmentsPerMessage, text.maxImagesPerMessage),
        })
        .superRefine((data, context) => {
          if (data.message.trim().length === 0 && data.files.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: text.addMessageOrImage,
              path: ['message'],
            });
          }
        }),
    [
      maxAttachmentsPerMessage,
      text.addMessageOrImage,
      text.maxImagesPerMessage,
    ],
  );

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<ReplyTicketFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      message: '',
      files: [],
    },
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const files = watch('files');
  const disabled = isDisabled || isSubmitting;

  function handleFilesSelection(incomingFiles: File[]) {
    const updatedFiles = [...files];

    for (const file of incomingFiles) {
      if (!allowedMimeTypes.includes(file.type)) {
        toast.error(text.invalidFileType(file.name));
        continue;
      }

      if (file.size > maxImageSizeBytes) {
        toast.error(text.maxFileSize(file.name));
        continue;
      }

      const alreadyAdded = updatedFiles.some((addedFile) =>
        areSameFile(addedFile, file),
      );

      if (alreadyAdded) {
        continue;
      }

      if (updatedFiles.length >= maxAttachmentsPerMessage) {
        toast.error(text.maxImagesPerMessage);
        break;
      }

      updatedFiles.push(file);
    }

    setValue('files', updatedFiles, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const onValidSubmit = handleSubmit(async (values) => {
    const succeeded = await onSubmit(values);

    if (succeeded) {
      reset({
        message: '',
        files: [],
      });
    }
  });

  return (
    <form className="space-y-3 border-t pt-4" onSubmit={onValidSubmit}>
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-gray-500" />
        <p className="text-sm font-medium text-gray-700">
          {text.replyTicketTitle}
        </p>
      </div>

      {showBlockedMessage && blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {blockedMessage}
        </p>
      )}

      <Textarea
        placeholder={text.replyPlaceholder}
        className="min-h-[100px]"
        disabled={disabled}
        {...register('message')}
      />

      {errors.message?.message && (
        <p className="text-xs text-red-600">{errors.message.message}</p>
      )}

      <div className="space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedMimeTypes.join(', ')}
          multiple
          onChange={(event) => {
            handleFilesSelection(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
          disabled={disabled}
          className="hidden"
        />

        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="h-4 w-4" />
          {text.replyAttachFilesButton}
        </Button>

        <p className="text-xs text-gray-500">{text.replyImagesHint}</p>

        {errors.files?.message && (
          <p className="text-xs text-red-600">{errors.files.message}</p>
        )}

        <SelectedFilesList
          files={files}
          disabled={disabled}
          onRemove={(index) => {
            setValue(
              'files',
              files.filter((_, currentIndex) => currentIndex !== index),
              {
                shouldDirty: true,
                shouldValidate: true,
              },
            );
          }}
        />
      </div>

      <Button type="submit" className="gap-2" disabled={disabled}>
        {isSubmitting ? (
          <LoaderCircle className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
        {text.sendMessageButton}
      </Button>
    </form>
  );
}
